import { Link, useNavigate, useLocation } from "react-router-dom";
import { Phone, Mail, MapPin, Instagram, Facebook, Linkedin } from "lucide-react";
import { LOGO_ICON } from "@/lib/data";

export default function Footer() {
  const navigate = useNavigate();
  const location = useLocation();

  const goSection = (id) => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 150);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-secondary text-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3 mb-5">
            <img src={LOGO_ICON} alt="Logo" className="h-16 w-16 object-contain bg-white/95 rounded-lg p-1" />
            <div className="leading-tight">
              <p className="font-heading text-xl font-semibold">Cardiac Precision</p>
              <p className="text-[11px] tracking-[0.22em] uppercase text-blue-300 font-bold">Diagnostics, LLC</p>
            </div>
          </div>
          <p className="text-white/70 text-sm leading-relaxed max-w-sm">
            Mobile cardiovascular diagnostic imaging delivered directly to physician
            offices, clinics, and healthcare facilities across Greater Houston, Texas.
          </p>
          <p className="text-white/60 text-xs leading-relaxed max-w-sm mt-4">
            Every examination and image transmission is performed in strict compliance
            with HIPAA regulations and industry best practices.
          </p>
        </div>

        <div className="md:col-span-3">
          <h4 className="text-xs tracking-[0.2em] uppercase font-bold text-blue-300 mb-5">Explore</h4>
          <ul className="space-y-3 text-sm text-white/80">
            <li><button data-testid="footer-services" onClick={() => goSection("services")} className="hover:text-white transition-colors duration-200">Services</button></li>
            <li><button data-testid="footer-areas" onClick={() => goSection("areas")} className="hover:text-white transition-colors duration-200">Service Areas</button></li>
            <li><Link data-testid="footer-kyh" to="/know-your-heart" className="hover:text-white transition-colors duration-200">Know Your Heart</Link></li>
            <li><button data-testid="footer-faq" onClick={() => goSection("faq")} className="hover:text-white transition-colors duration-200">FAQ</button></li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <h4 className="text-xs tracking-[0.2em] uppercase font-bold text-blue-300 mb-5">Contact</h4>
          <ul className="space-y-4 text-sm text-white/80">
            <li><a data-testid="footer-phone" href="tel:3465384241" className="flex items-center gap-3 hover:text-white transition-colors duration-200"><Phone size={16} /> 346-538-4241</a></li>
            <li><a data-testid="footer-email" href="mailto:info@cardiacprecisiondiagnostics.com" className="flex items-center gap-3 hover:text-white transition-colors duration-200"><Mail size={16} /> info@cardiacprecisiondiagnostics.com</a></li>
            <li className="flex items-start gap-3" data-testid="footer-address">
              <MapPin size={16} className="mt-0.5 shrink-0" />
              <span>
                Cardiac Precision Diagnostics<br />
                <span className="text-white/55">at MedFlex Spaces</span><br />
                7616 Branford Place, Suite 220<br />
                Sugar Land, TX 77479
              </span>
            </li>
            <li className="flex items-start gap-3"><MapPin size={16} className="mt-0.5 shrink-0" /><span><span className="text-[11px] tracking-[0.2em] uppercase font-bold text-blue-300 block mb-1">Mobile Service Area</span>Houston, Katy, Sugar Land, Richmond, Pearland &amp; surrounding Greater Houston areas</span></li>
          </ul>
          <div className="mt-6">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-blue-300 mb-3">Follow Us</p>
            <div className="flex items-center gap-3">
              <a data-testid="social-instagram" href="https://www.instagram.com/cardiacprecisiondiagnostics" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-primary transition-colors duration-200">
                <Instagram size={18} />
              </a>
              <a data-testid="social-facebook" href="https://www.facebook.com/share/19RusxAwd8/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-primary transition-colors duration-200">
                <Facebook size={18} />
              </a>
              <a data-testid="social-linkedin" href="https://www.linkedin.com/company/cardiac-precision-diagnostics-llc/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-primary transition-colors duration-200">
                <Linkedin size={18} />
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/50">
          <p>© {new Date().getFullYear()} Cardiac Precision Diagnostics, LLC. All rights reserved.</p>
          <Link data-testid="admin-link" to="/admin" className="hover:text-white/80 transition-colors duration-200">Admin</Link>
        </div>
      </div>
    </footer>
  );
}
